import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { useIntl } from 'react-intl';
import uniqBy from 'lodash/uniqBy';
import AdditiveFilter from '@fuww/library/src/ProductFilters/AdditiveFilter';
import BrandFilter
  from '@fuww/library/src/ProductFilters/BrandFilter';
import messages from '../../lib/messages.mjs';
import loadMore from '../../lib/queries/loadMore';
import withQuery from '../../lib/withQuery';
import LoadMore from '../LoadMore';
import getBrandsBySelected from '../../lib/getBrandsBySelected';
import productBrandsConnectionQuery
  from '../../lib/queries/products/productBrandsConnectionQuery.graphql';
import { ProductBrandsConnectionQuery } from '../../lib/graphql/api/graphql';

const brandsDialogNodePropertyTypes = {
  node: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    profiles: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
};

const brandsDialogPropertyTypes = {
  variables: PropTypes.shape({
    brandIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  }).isRequired,
  fetchMore: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  queryParams: PropTypes.shape({}).isRequired,
  closeDialog: PropTypes.func.isRequired,
  selectedBrands: PropTypes.arrayOf(PropTypes.shape(
    brandsDialogNodePropertyTypes,
  ).isRequired).isRequired,
  open: PropTypes.bool,
};

type BrandsDialogProperties = InferProps<
  typeof brandsDialogPropertyTypes
> & {
  data: ProductBrandsConnectionQuery;
};

const BrandsDialog = ({
  data,
  variables,
  fetchMore,
  loading,
  queryParams,
  open,
  closeDialog,
  selectedBrands,
}: BrandsDialogProperties) => {
  const intl = useIntl();

  const {
    productBrandsConnection: { edges, pageInfo: { hasNextPage } },
  } = data;

  return (
    <div>
      <BrandFilter
        closeDialog={closeDialog}
        open={open}
      >
        <AdditiveFilter
          title={intl.formatMessage(messages['products.brands'])}
          choices={
            getBrandsBySelected(
              variables,
              uniqBy(
                [...selectedBrands, ...edges]
                  .map((edge) => ({ id: edge.node.id, name: edge.node.name })),
                ({ id }) => id,
              ),
              queryParams,
            )
          }
          selected={variables.brandIds}
        >
          <LoadMore
            loading={loading}
            hasNextPage={hasNextPage}
            fetchMore={
              () => loadMore(
                { variables },
              )(data, 'productBrandsConnection', fetchMore)
            }
          />
        </AdditiveFilter>
      </BrandFilter>
    </div>
  );
};

BrandsDialog.propTypes = brandsDialogPropertyTypes;

const BrandsDialogWithQuery = withQuery(BrandsDialog);

const brandsDialogWithSelectedPropertyTypes = {
  data: PropTypes.shape({
    brandsConnection: PropTypes.shape({
      edges: PropTypes.arrayOf(
        PropTypes.shape(brandsDialogNodePropertyTypes).isRequired,
      ).isRequired,
    }).isRequired,
  }).isRequired,
  variables: PropTypes.shape({}).isRequired,
  queryParams: PropTypes.shape({}).isRequired,
  closeDialog: PropTypes.func.isRequired,
  open: PropTypes.bool,
};

type BrandsDialogWithSelectedProperties = InferProps<
  typeof brandsDialogWithSelectedPropertyTypes
>;

const BrandsDialogWithSelected = ({
  data,
  variables,
  queryParams,
  open = true,
  closeDialog,
}: BrandsDialogWithSelectedProperties) => (
  <BrandsDialogWithQuery
    query={productBrandsConnectionQuery}
    errorMessage="Error loading more brands."
    variables={{
      ...variables,
      includeProfile: false,
    }}
    queryParams={queryParams}
    open={open}
    closeDialog={closeDialog}
    selectedBrands={data.brandsConnection.edges}
    showLoaderWhenSetVariables={false}
  />
);

BrandsDialogWithSelected.propTypes = brandsDialogWithSelectedPropertyTypes;

export default BrandsDialogWithSelected;
